import { Box, Card } from '@mantine/core';
import { type ReactElement, useEffect, useState } from 'react';
import { Node } from 'reactflow';
import {
    DialogSize,
    ResizableDialog,
} from '../../../../../../react-commons/components/data-display/dialog/ResizableDialog';
import { QueryBaseComponent } from '../../../../../../react-commons/query/QueryBaseComponent';
import { OpenModal } from '../../../../../../react-commons/utils/modals/openModal';
import { SplitPane } from '../../../../../../react-commons/views/split-pane/SplitPane';
import { useProjectActions } from '../../../../resource/resources/artifacts/project/projectResource';
import { useProjectVersionActions } from '../../../../resource/resources/artifacts/project/projectVersionResource';
import { VersionGraph } from '../../../reusable-components/version-graph/VersionGraph';
import { useMyNavigate } from '../../../router/createdRouter';
import { ProjectVersionDetailsComponent } from './ProjectVersionDetailsComponent';

export const ProjectVersionGraphDialog = ({
    onClose,
    projectId,
    noActions,
    activeId,
}: {
    onClose: () => void;
    projectId: string;
    activeId: string;
    noActions?: boolean;
}) => {
    const [selectedActiveVersionId, setSelectedActiveVersionId] = useState<string>(activeId);
    const [selectedVersionId, setSelectedVersionId] = useState<string>(activeId);
    const [withVariants, setWithVariants] = useState<boolean>(false);

    const navigate = useMyNavigate();

    const projectVersionActions = useProjectVersionActions();
    const projectAdapter = useProjectActions();

    const createNewVersion = projectVersionActions.useCreateNewVersion();
    const graphVersionDiagramQuery = projectVersionActions.useGetVersionGraph(projectId);
    const project = projectAdapter.useGetResource(projectId);

    useEffect(() => {
        if (
            selectedVersionId === '' &&
            graphVersionDiagramQuery.data &&
            (graphVersionDiagramQuery.data?.length ?? 0) > 0
        ) {
            const version = graphVersionDiagramQuery.data[0];
            if (version) {
                setSelectedVersionId(version.versionId);
            }
        }
    }, [graphVersionDiagramQuery.data]);

    const getNodeContextMenuItems = (
        node: Node
    ): { onClick: () => void; title: string; key: string; disabled?: boolean }[] => {
        if (noActions) {
            return [
                {
                    key: 'load_version',
                    title: 'Load',
                    onClick: () => {
                        setSelectedActiveVersionId(node.id);
                        navigate({
                            route: '/project/:id/active-version/:versionId',
                            routeParams: { id: projectId, versionId: node.id },
                        });
                    },
                },
            ];
        }
        return [
            {
                key: 'create_new_version',
                title: 'Create New Version',
                onClick: () => {
                    openAlertDialog(
                        `Are you sure you want to create a new version from version ${node.data.label}?`,
                        () => {
                            createNewVersion.mutate({
                                precedingVersion: node.id,
                            });
                        }
                    );
                },
            },
            {
                key: 'load_version',
                title: 'Load',
                onClick: () => {
                    setSelectedActiveVersionId(node.id);
                    navigate({
                        route: '/project/:id/active-version/:versionId',
                        routeParams: { id: projectId, versionId: node.id },
                    });
                },
            },
        ];
    };

    const openAlertDialog = (text: ReactElement | string, action: () => void) => {
        OpenModal.AlertModal('Warning', text, action);
    };

    return (
        <ResizableDialog
            title={`Project Version History: ${project.data?.name}`}
            onClose={onClose}
            canClose={true}
            dialogSize={DialogSize.LARGE}
            disableBackdropClick={true}
        >
            <QueryBaseComponent name={'Version Graph'} query={graphVersionDiagramQuery}>
                <SplitPane secondaryInitialSize={70}>
                    <Card w={'100%'} h={'100%'} p={8} withBorder>
                        <Box pos={'relative'} w={'100%'} h={'100%'}>
                            <VersionGraph
                                graphVersionDiagramQuery={graphVersionDiagramQuery}
                                onNodeClick={(nodeId, _) => {
                                    setSelectedVersionId(nodeId);
                                }}
                                getNodeContextMenuItems={getNodeContextMenuItems}
                                withVariants={withVariants}
                                activeId={selectedActiveVersionId}
                            />
                        </Box>
                    </Card>
                    <ProjectVersionDetailsComponent projectId={projectId} projectVersionId={selectedVersionId} />
                </SplitPane>
            </QueryBaseComponent>
        </ResizableDialog>
    );
};
